import React, { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { X, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';

const TUTORIAL_STEPS = [
  {
    target: '[data-testid="local-editor-modal"]',
    title: 'Welcome to the Final Editor',
    body: 'This is your local manuscript intelligence engine. It analyzes your writing using rule-based logic — no AI APIs, no cost per scan. Everything runs locally and deterministically.',
    position: 'center',
    highlight: null,
  },
  {
    target: '[data-testid="start-scan-btn"]',
    title: 'Start a Scan',
    body: 'Click here to run the full editorial pipeline. Choose your genre profile and sensitivity level. The engine will parse your manuscript, build story memory, detect issues, and suggest rewrites.',
    position: 'bottom',
    highlight: 'start-scan-btn',
  },
  {
    target: '[data-testid="tab-issues"]',
    title: 'Issues Tab',
    body: 'This is where all detected issues appear — spelling, punctuation, repetition, continuity, style, and rewrite suggestions. Issues are grouped by severity and category. You can apply fixes, dismiss informational items, or add words to your custom dictionary.',
    position: 'bottom',
    highlight: 'tab-issues',
  },
  {
    target: '[data-testid="tab-memory"]',
    title: 'Story Memory',
    body: 'The engine extracts characters, locations, objects, and timeline events from your manuscript. This memory is used to detect continuity errors — like a character with blue eyes in Chapter 1 having green eyes in Chapter 5.',
    position: 'bottom',
    highlight: 'tab-memory',
  },
  {
    target: '[data-testid="tab-voice"]',
    title: 'Voice Profile',
    body: 'Your unique writing fingerprint. The engine measures sentence length patterns, dialogue ratio, paragraph rhythm, and fragment usage to understand your natural style — then avoids flagging things that are intentional.',
    position: 'bottom',
    highlight: 'tab-voice',
  },
  {
    target: '[data-testid="tab-scenes"]',
    title: 'Scene Map',
    body: 'Visual breakdown of your manuscript into scenes. Shows pacing type (action, dialogue, introspection), estimated tension, and scene transitions. Helps identify pacing lulls or chapters that feel unbalanced.',
    position: 'bottom',
    highlight: 'tab-scenes',
  },
  {
    target: '[data-testid="tab-pacing"]',
    title: 'Pacing Analysis',
    body: 'Chapter-by-chapter pacing breakdown showing dialogue vs. narration balance, action density, and rhythm patterns. Flags chapters that are dialogue-heavy without grounding or exposition-heavy without interaction.',
    position: 'bottom',
    highlight: 'tab-pacing',
  },
  {
    target: '[data-testid="tab-scores"]',
    title: 'Chapter Scores',
    body: 'Every chapter gets scored on spelling, punctuation, continuity, repetition, and style. Plus tension estimation and sensory balance analysis. Scores are heuristic — they highlight patterns, not judge your writing.',
    position: 'bottom',
    highlight: 'tab-scores',
  },
  {
    target: '[data-testid="tab-report"]',
    title: 'Full Report',
    body: 'A comprehensive editorial summary of your entire manuscript. Shows readiness level, tier breakdown, voice fingerprint, and pacing summary. Use this to gauge overall manuscript health.',
    position: 'bottom',
    highlight: 'tab-report',
  },
  {
    target: '[data-testid="auto-fix-all-btn"]',
    title: 'Auto-Fix All',
    body: 'Applies all safe, high-confidence fixes at once — things like double spaces, repeated words, and obvious misspellings. Only Class A (auto-safe) fixes are applied. Your voice is never touched.',
    position: 'bottom',
    highlight: 'auto-fix-all-btn',
  },
  {
    target: '[data-testid="dismiss-info-btn"]',
    title: 'Dismiss Informational',
    body: 'Clears all informational observations that have no fix — like readability scores or pacing notes. They are acknowledged but not actionable. Reduces noise so you can focus on real issues.',
    position: 'bottom',
    highlight: 'dismiss-info-btn',
  },
  {
    target: null,
    title: 'Issue Categories Explained',
    body: 'Auto-Fix Safe: Spelling, repeated words, punctuation — safe to apply.\nSuggestions: Style improvements, rewrite options — review before applying.\nContinuity Flags: Character, location, timeline contradictions — check your manuscript.\nNarrative Warnings: Pacing, tension, gesture overuse — consider revising.\nRewrite Options: Filler removal, weak phrasing tightening — your choice.',
    position: 'center',
    highlight: null,
  },
  {
    target: null,
    title: 'You\'re Ready!',
    body: 'The Final Editor is designed to be useful, honest, and non-destructive. It will never claim to "understand" your story — instead it provides data-driven observations and safe mechanical fixes. Happy editing!',
    position: 'center',
    highlight: null,
  },
];

const EditorTutorial = ({ onClose, isVisible }) => {
  const [step, setStep] = useState(0);
  const current = TUTORIAL_STEPS[step];
  const total = TUTORIAL_STEPS.length;

  const next = useCallback(() => {
    if (step < total - 1) setStep(s => s + 1);
    else onClose();
  }, [step, total, onClose]);

  const prev = useCallback(() => {
    if (step > 0) setStep(s => s - 1);
  }, [step]);

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 z-[100] transition-opacity" onClick={onClose} />

      {/* Highlight pulse */}
      {current.highlight && (
        <style>{`
          [data-testid="${current.highlight}"] {
            position: relative;
            z-index: 101;
            box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.2);
            border-radius: 8px;
            animation: tutorialPulse 2s ease-in-out infinite;
          }
          @keyframes tutorialPulse {
            0%, 100% { box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.2); }
            50% { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0.2), 0 0 30px rgba(16, 185, 129, 0.3); }
          }
        `}</style>
      )}

      {/* Tutorial Card */}
      <div className="fixed z-[102] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md px-4"
        data-testid="editor-tutorial-card">
        <Card className="bg-slate-900 border-emerald-500/30 shadow-2xl shadow-emerald-500/10">
          <CardContent className="p-5">
            {/* Progress bar */}
            <div className="flex items-center gap-1 mb-3">
              {TUTORIAL_STEPS.map((_, i) => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-all ${
                  i <= step ? 'bg-emerald-500' : 'bg-slate-700'
                }`} />
              ))}
            </div>

            {/* Step counter */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-emerald-400 font-medium tracking-wide uppercase">
                Step {step + 1} of {total}
              </span>
              <Button size="sm" variant="ghost" onClick={onClose}
                className="h-6 w-6 p-0 text-slate-500 hover:text-white"
                data-testid="tutorial-close-btn">
                <X className="w-3 h-3" />
              </Button>
            </div>

            {/* Content */}
            <h3 className="text-base font-semibold text-white mb-2">{current.title}</h3>
            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{current.body}</p>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-700/50">
              <Button size="sm" variant="ghost" onClick={prev} disabled={step === 0}
                className="text-slate-400 hover:text-white text-xs h-8"
                data-testid="tutorial-prev-btn">
                <ChevronLeft className="w-3 h-3 mr-1" /> Previous
              </Button>

              <Button size="sm" onClick={next}
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8 px-4"
                data-testid="tutorial-next-btn">
                {step === total - 1 ? 'Finish' : (
                  <>Next <ChevronRight className="w-3 h-3 ml-1" /></>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

// Trigger button for the tutorial
export const TutorialTrigger = ({ onClick }) => (
  <Button size="sm" variant="ghost" onClick={onClick}
    className="text-slate-400 hover:text-emerald-400 text-xs h-7 gap-1"
    data-testid="tutorial-trigger-btn"
    title="Interactive guide — learn what each tab does">
    <HelpCircle className="w-3.5 h-3.5" /> Guide
  </Button>
);

export default EditorTutorial;
